// ts-rest contract for the 8-stage onboarding pipeline (PRD #69).
//
// The pipeline takes a prospect from initial enquiry through to a fully
// onboarded investing client. Endpoints under /api/onboarding/*:
//   GET  /api/onboarding/cases               — list cases (optional filters)
//   GET  /api/onboarding/cases/:id           — single case detail
//   POST /api/onboarding/cases               — create a new enquiry (stage 1)
//   POST /api/onboarding/cases/:id/remove     — remove a case (reason required)
//   POST /api/onboarding/cases/:id/transition — move a case between stages
//   POST /api/onboarding/cases/:id/kyc-check  — record the stage-4 KYC step
//   POST /api/onboarding/cases/:id/post-meeting-note — capture a post-meeting note
//   POST /api/onboarding/cases/:id/fact-find/remind  — send a fact-find reminder
//   GET  /api/onboarding/analytics            — pipeline analytics rollup

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// ---------------------------------------------------------------------------
// Stage + status enums
// ---------------------------------------------------------------------------

/** The eight canonical onboarding pipeline stages. */
export const onboardingPipelineStageSchema = z.enum([
    'enquiry',
    'first_meeting',
    'fact_find',
    'kyc_aml',
    'suitability',
    'signing',
    'account_opening',
    'portal_access',
]);
export type OnboardingPipelineStage = z.infer<typeof onboardingPipelineStageSchema>;

export const onboardingCaseStatusSchema = z.enum(['active', 'stalled', 'completed']);
export type OnboardingPipelineCaseStatus = z.infer<typeof onboardingCaseStatusSchema>;

export const onboardingEnquirySourceSchema = z.enum([
    'IFA referral',
    'Client referral',
    'Professional introducer',
    'Direct enquiry',
    'Event',
]);
export type OnboardingEnquirySource = z.infer<typeof onboardingEnquirySourceSchema>;

export const kycResultStatusSchema = z.enum(['pass', 'refer', 'fail']);
export type KycResultStatus = z.infer<typeof kycResultStatusSchema>;

// ---------------------------------------------------------------------------
// Stage artefacts
// ---------------------------------------------------------------------------

export const postMeetingNoteSchema = z.object({
    capturedAt: z.string(),
    capturedBy: z.string(),
    clientAgenda: z.string(),
    classification: z.enum(['retail', 'professional']),
    hnwStatus: z.boolean(),
    conflictsCheck: z.string(),
    documentsHandedOver: z.array(z.string()),
});
export type PostMeetingNote = z.infer<typeof postMeetingNoteSchema>;

export const kycResultSchema = z.object({
    status: kycResultStatusSchema,
    vendor: z.string(),
    reference: z.string(),
    screenedAt: z.string(),
    sanctionsHits: z.number().int(),
    pepHits: z.number().int(),
    adverseMediaHits: z.number().int(),
    evidence: z.array(z.string()),
    eddRequired: z.boolean(),
});
export type KycResult = z.infer<typeof kycResultSchema>;

export const sourceOfFundsDeclarationSchema = z.object({
    declaredAt: z.string(),
    declaredBy: z.string(),
    primarySource: z.string(),
    narrative: z.string(),
    supportingEvidence: z.array(z.string()),
});
export type SourceOfFundsDeclaration = z.infer<typeof sourceOfFundsDeclarationSchema>;

// ---------------------------------------------------------------------------
// Audit + case
// ---------------------------------------------------------------------------

export const onboardingAuditEntrySchema = z.object({
    id: z.string(),
    from: onboardingPipelineStageSchema.nullable(),
    to: onboardingPipelineStageSchema,
    at: z.string(),
    by: z.string(),
    note: z.string().optional(),
});
export type OnboardingAuditEntry = z.infer<typeof onboardingAuditEntrySchema>;

export const onboardingPipelineCaseSchema = z.object({
    id: z.string(),
    caseRef: z.string(),
    /**
     * Canonical `Client.id` this case owns — the prospect is modelled as a
     * `Client(onboarding:true)` from enquiry and linked via
     * `onboarding_pipeline_cases.client_id`. Surfaced on the wire so the
     * frontend can filter the prospect's calls and link to the Fact Find page.
     * Nullable/optional: a partial / unlinked row carries none.
     */
    clientId: z.string().nullable().optional(),
    prospectName: z.string(),
    applicantType: z.enum(['Individual', 'Joint', 'Trust', 'Entity']),
    stage: onboardingPipelineStageSchema,
    status: onboardingCaseStatusSchema,
    /** ISO timestamp the case entered its current stage. Drives SLA + WARDEN. */
    stageEnteredAt: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    // Commercial
    source: onboardingEnquirySourceSchema,
    referrer: z.string().optional(),
    initialAum: z.number(),
    mandate: z.string(),
    email: z.string(),
    phone: z.string().optional(),
    // Ownership
    assignedIm: z.string(),
    assignedImId: z.string().optional(),
    // Stage 3 — fact find
    factFindStatus: z.enum(['not_sent', 'sent', 'in_progress', 'complete']),
    factFindReminders: z.number().int(),
    factFindGaps: z.array(z.string()),
    // Stage-specific artefacts
    postMeetingNote: postMeetingNoteSchema.optional(),
    kycResult: kycResultSchema.optional(),
    sourceOfFunds: sourceOfFundsDeclarationSchema.optional(),
    // Stage 5 — suitability. Gate status for `suitability → signing`, DERIVED
    // from the latest Internal Suitability Assessment for the case's client: no
    // assessment → not_started, a working draft → draft, a signed-off →
    // completed (which clears the gate).
    suitabilityAssessmentStatus: z.enum(['not_started', 'draft', 'completed']).optional(),
    portalProvisioned: z.boolean().optional(),
    welcomePackSentAt: z.string().optional(),
    // Misc
    blockingReason: z.string().optional(),
    agentTaskId: z.string().optional(),
    auditLog: z.array(onboardingAuditEntrySchema),
    // Terminal soft-removal (RemoveOnboardingCase). A removed case is retained
    // for audit but excluded from the list endpoint (and therefore the kanban,
    // counts and analytics); `getCase` still returns it so the detail page can
    // show the removed banner. `removedAt` null/absent on a live case.
    removedAt: z.string().optional(),
    removalReason: z.string().optional(),
    removedByUserId: z.string().optional(),
});
export type OnboardingPipelineCase = z.infer<typeof onboardingPipelineCaseSchema>;

// ---------------------------------------------------------------------------
// Request / response schemas
// ---------------------------------------------------------------------------

export const onboardingListResponseSchema = z.object({
    cases: z.array(onboardingPipelineCaseSchema),
});
export type OnboardingListResponse = z.infer<typeof onboardingListResponseSchema>;

const onboardingCaseResponseSchema = z.object({
    ok: z.boolean(),
    case: onboardingPipelineCaseSchema,
});
export type OnboardingCaseResponse = z.infer<typeof onboardingCaseResponseSchema>;

export const createEnquiryRequestSchema = z.object({
    prospectName: z.string().min(1),
    applicantType: z.enum(['Individual', 'Joint', 'Trust', 'Entity']).default('Individual'),
    source: onboardingEnquirySourceSchema,
    referrer: z.string().optional(),
    initialAum: z.number().nonnegative(),
    mandate: z.string().default('Balanced Growth'),
    email: z.string(),
    phone: z.string().optional(),
    /**
     * Optional preferred IM name. When supplied (the desk persona creating an
     * enquiry on their own book, or a Comply officer choosing one) the case is
     * assigned to them; otherwise the case is left unassigned, awaiting triage.
     */
    preferredIm: z.string().optional(),
    /** Stable id of the preferred IM (the `users` row), captured when chosen. */
    preferredImId: z.string().optional(),
});
export type CreateEnquiryRequest = z.infer<typeof createEnquiryRequestSchema>;

export const assignImRequestSchema = z.object({
    imName: z.string().min(1),
    imId: z.string().optional(),
    by: z.string(),
});
export type AssignImRequest = z.infer<typeof assignImRequestSchema>;

export const transitionRequestSchema = z.object({
    to: onboardingPipelineStageSchema,
    by: z.string(),
    note: z.string().optional(),
});
export type TransitionRequest = z.infer<typeof transitionRequestSchema>;

export const postMeetingNoteRequestSchema = z.object({
    capturedBy: z.string(),
    clientAgenda: z.string().min(1),
    classification: z.enum(['retail', 'professional']),
    hnwStatus: z.boolean(),
    conflictsCheck: z.string(),
    documentsHandedOver: z.array(z.string()).default([]),
});
export type PostMeetingNoteRequest = z.infer<typeof postMeetingNoteRequestSchema>;

export const remindRequestSchema = z.object({
    by: z.string(),
    channel: z.enum(['email', 'sms']).default('email'),
});
export type RemindRequest = z.infer<typeof remindRequestSchema>;

export const actorRequestSchema = z.object({ by: z.string() });
export type ActorRequest = z.infer<typeof actorRequestSchema>;

/**
 * Body for the stage-4 KYC/AML check. `outcome` lets the operator record the
 * screening verdict (pass / refer / fail). There is no live KYC vendor in dev,
 * so this drives the deterministic result the server synthesises; it defaults
 * to `pass` when omitted. A `refer` / `fail` result blocks the
 * `kyc_aml → suitability` transition until cleared.
 */
export const kycCheckRequestSchema = actorRequestSchema.extend({
    outcome: kycResultStatusSchema.optional(),
});
export type KycCheckRequest = z.infer<typeof kycCheckRequestSchema>;

/** Remove a case from the pipeline — the reason note is mandatory. */
export const removeCaseRequestSchema = z.object({
    reason: z.string().trim().min(1),
    by: z.string(),
});
export type RemoveCaseRequest = z.infer<typeof removeCaseRequestSchema>;

// ---------------------------------------------------------------------------
// Reuse-or-run call dispatch (§7 First Meeting knowledge)
// ---------------------------------------------------------------------------

/**
 * First-Meeting Client-Knowledge prefill from a call. When the call already
 * carries an `aiSummary` / Client-Knowledge note, the post-meeting agenda is
 * prefilled from it (`reused: true`) instead of re-dispatching ATLAS.
 */
export const onboardingCallKnowledgeResponseSchema = z.object({
    /** True when an existing artefact was reused; false when ATLAS was dispatched. */
    reused: z.boolean(),
    /** Prefill text from the existing artefact — present only when `reused` is true. */
    prefill: z
        .object({
            agenda: z.string(),
            conflicts: z.string(),
        })
        .optional(),
});
export type OnboardingCallKnowledgeResponse = z.infer<typeof onboardingCallKnowledgeResponseSchema>;

export const onboardingStageMetricSchema = z.object({
    stage: onboardingPipelineStageSchema,
    count: z.number().int(),
    medianDays: z.number(),
    slaDays: z.number().int(),
    breachedSla: z.number().int(),
});
export type OnboardingStageMetric = z.infer<typeof onboardingStageMetricSchema>;

export const onboardingAnalyticsResponseSchema = z.object({
    totalCases: z.number().int(),
    activeCases: z.number().int(),
    completedCases: z.number().int(),
    stalledCases: z.number().int(),
    conversionRate: z.number(),
    stages: z.array(onboardingStageMetricSchema),
    imPerformance: z.array(
        z.object({
            im: z.string(),
            activeCases: z.number().int(),
            stalledCases: z.number().int(),
            completedCases: z.number().int(),
        }),
    ),
});
export type OnboardingAnalyticsResponse = z.infer<typeof onboardingAnalyticsResponseSchema>;

import { errorBodyWithCode as errorBody } from '../schemas/common';

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const onboardingContract = c.router(
    {
        listCases: {
            method: 'GET',
            path: '/onboarding/cases',
            query: z
                .object({
                    stage: onboardingPipelineStageSchema.optional(),
                    imId: z.string().optional(),
                    status: onboardingCaseStatusSchema.optional(),
                })
                .optional(),
            responses: { 200: onboardingListResponseSchema },
            summary: 'List onboarding pipeline cases',
        },
        getCase: {
            method: 'GET',
            path: '/onboarding/cases/:id',
            responses: { 200: onboardingCaseResponseSchema, 404: errorBody },
            summary: 'Get a single onboarding case',
        },
        createEnquiry: {
            method: 'POST',
            path: '/onboarding/cases',
            body: createEnquiryRequestSchema,
            responses: { 200: onboardingCaseResponseSchema, 400: errorBody },
            summary: 'Create a new stage-1 enquiry (assigned to the chosen IM, else unassigned)',
        },
        assignIm: {
            method: 'POST',
            path: '/onboarding/cases/:id/assign-im',
            body: assignImRequestSchema,
            responses: { 200: onboardingCaseResponseSchema, 400: errorBody, 404: errorBody },
            summary: 'Assign (or reassign) the investment manager on a case',
        },
        removeCase: {
            method: 'POST',
            path: '/onboarding/cases/:id/remove',
            body: removeCaseRequestSchema,
            responses: {
                200: onboardingCaseResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Remove a case from the pipeline (retained for audit, hidden from the board) — reason required',
        },
        transition: {
            method: 'POST',
            path: '/onboarding/cases/:id/transition',
            body: transitionRequestSchema,
            responses: {
                200: onboardingCaseResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Move a case between stages (prerequisites + stage-owner role enforced)',
        },
        capturePostMeetingNote: {
            method: 'POST',
            path: '/onboarding/cases/:id/post-meeting-note',
            body: postMeetingNoteRequestSchema,
            responses: {
                200: onboardingCaseResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'Capture a structured stage-2 post-meeting note',
        },
        remindFactFind: {
            method: 'POST',
            path: '/onboarding/cases/:id/fact-find/remind',
            body: remindRequestSchema,
            responses: {
                200: onboardingCaseResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'Send a stage-3 fact-find reminder (email/SMS stub)',
        },
        markFactFindComplete: {
            method: 'POST',
            path: '/onboarding/cases/:id/fact-find/complete',
            body: actorRequestSchema,
            responses: {
                200: onboardingCaseResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'Mark the stage-3 fact find complete',
        },
        runKycCheck: {
            method: 'POST',
            path: '/onboarding/cases/:id/kyc-check',
            body: kycCheckRequestSchema,
            responses: {
                200: onboardingCaseResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'Run the stage-4 KYC check',
        },
        provisionPortal: {
            method: 'POST',
            path: '/onboarding/cases/:id/portal/provision',
            body: actorRequestSchema,
            responses: {
                200: onboardingCaseResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'Provision stage-8 portal access',
        },
        prefillKnowledgeFromCall: {
            method: 'POST',
            path: '/onboarding/cases/:id/calls/:callId/knowledge',
            body: z.object({}).optional(),
            responses: {
                200: onboardingCallKnowledgeResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'First Meeting: reuse the call’s Client-Knowledge for the post-meeting note, or dispatch ATLAS when absent',
        },
        analytics: {
            method: 'GET',
            path: '/onboarding/analytics',
            query: z.object({ imId: z.string().optional() }).optional(),
            responses: { 200: onboardingAnalyticsResponseSchema },
            summary: 'Pipeline analytics rollup (funnel, median days, SLA, stalls)',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
